/**
 * # After Install
 */

/**
 * ## Imports
 */
import './AfterDownloadPage.scss';

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useHistory } from 'react-router-dom';

import NavigationBar from '../../common/components/NavigationBar/NavigationBar';
import PageWrapperDark from '../../common/components/PageWrapperDark/PageWrapperDark';

import { useQuery } from '../../common/hooks/useQuery';
import breakpoints from '../../constants/breakpoints';
import { browserLinks, getOS, isChrome, isEdge } from '../../constants/userAgent';
import AppStoreQrCode from '../../img/afterDownload/app-store-qr-code.png';

/**
 * ## Component
 * @returns React
 */
function AfterDownload() {
    const { t } = useTranslation('websiteV2');
    const query = useQuery();
    const history = useHistory();

    const isDesktop = useMediaQuery({
        minWidth: breakpoints.minWidthMdBreakPoint,
    });

    useEffect(() => {
        const fromQuery = query.get('os');
        const downloadUrl = browserLinks[fromQuery || getOS(history.location.pathname === '/download')];

        if (downloadUrl) {
            window.location.href = downloadUrl;
        } else {
            history.push('/browser');
        }
    }, [history, query]);

    return (
        <>
            <Helmet>
                <title>Install Browser</title>
                <meta
                    name="description"
                    content="On a typical browser, websites can watch your every move. But Tempest blocks tracking, profiling and fingerprinting, for next-level privacy and control."
                />
            </Helmet>
            <div className="homepage homepage--interior">
                <PageWrapperDark isDisableScroll>
                    <NavigationBar onDarkBackground={true} />
                    <section className="home-centerwrapper">
                        <div className="main-content">
                            <p className="main-content__paragraph main-content__tagline">
                                {t('after-download-page-slogon', 'Almost there!')}
                            </p>
                            {isDesktop && (
                                <h1 className="main-content__title">
                                    {t('after-download-page-title', 'Now open the file that just downloaded.')}
                                </h1>
                            )}
                            <p className="main-content__paragraph main-content__try-again-text">
                                {t('after-download-page-subtitle-1', 'Your download should begin automatically.')}
                                <br />
                                {/*TODO: https://stackoverflow.com/a/61723308*/}
                                <Trans
                                    ns="websiteV2"
                                    i18nKey="after-download-page-subtitle-2"
                                    defaults={`Didn’t work? <a href="${
                                        browserLinks[query.get('os') || getOS()]
                                    }">Try downloading again</a>`}
                                    values={{
                                        tryAgainLink: `<a href="${browserLinks[query.get('os') || getOS()]}">${t(
                                            'after-download-page-try-again-link',
                                            'Try downloading again',
                                        )}</a>`,
                                    }}
                                    components={{ a: <a /> }}
                                />
                            </p>
                        </div>
                        <div className={`instructions ${(!isChrome || isEdge) && 'content-right'}`}>
                            {isChrome && !isEdge && (
                                <div className="instructions__browser-install">
                                    <p className="instructions__browser-install__text">
                                        {t('after-download-page-tempest-install', 'Click install Tempest')}
                                    </p>
                                </div>
                            )}
                            {isDesktop && (
                                <div className="instructions__mobile-qr-code">
                                    <div className="instructions__mobile-qr-code__inner">
                                        <h4 className="instructions__mobile-qr-code__inner__title">
                                            {t('after-download-page-qr-code-title', 'Power back in your hands')}
                                        </h4>
                                        <p className="instructions__mobile-qr-code__inner__subtitle">
                                            {t(
                                                'after-download-page-qr-code-subtitle',
                                                'Download Tempest mobile for privacy on the go.',
                                            )}
                                        </p>
                                    </div>
                                    <picture>
                                        <img alt="App Store QR Code" src={AppStoreQrCode} width="136" height="136" />
                                    </picture>
                                </div>
                            )}
                        </div>
                    </section>
                </PageWrapperDark>
            </div>
        </>
    );
}

export default AfterDownload;
